import { useState, useEffect } from 'react';
import { useUserContext } from './userContext';

const getEmailVerifiedFlag = (value: unknown): boolean | null => {
  const normalized = String(value ?? "").toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
};

export interface DesignerOnboardingStep {
  step: number;
  name: string;
  isAccessible: boolean;
  isCompleted: boolean;
  requiredStatus: string;
}

export const useDesignerOnboardingValidation = () => {
  const { user } = useUserContext();
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [steps, setSteps] = useState<DesignerOnboardingStep[]>([
    {
      step: 1,
      name: 'Email & Password',
      isAccessible: true,
      isCompleted: false,
      requiredStatus: 'any'
    },
    {
      step: 2,
      name: 'Email Verification',
      isAccessible: true,
      isCompleted: false,
      requiredStatus: 'any'
    },
    {
      step: 3,
      name: 'Personal Details',
      isAccessible: false,
      isCompleted: false,
      requiredStatus: 'pending_profile_completion'
    },
    {
      step: 4,
      name: 'Business Information',
      isAccessible: false,
      isCompleted: false,
      requiredStatus: 'pending_profile_completion'
    },
    {
      step: 5,
      name: 'Account Preferences',
      isAccessible: false,
      isCompleted: false,
      requiredStatus: 'pending_profile_completion'
    }
  ]);

  useEffect(() => {
    if (!user) return;

    const userStatus = user.onboardingStatus || 'pending_profile_completion';
    
    // Update step accessibility based on user status for designers
    const updatedSteps = steps.map(step => {
      let isAccessible = false;
      let isCompleted = false;

      switch (step.step) {
        case 1: // Email & Password
          isAccessible = true;
          isCompleted = true; // Always completed if user exists
          break;
        case 2: // Email Verification
          isAccessible = true;
          isCompleted = getEmailVerifiedFlag(user.emailVerified) === true || userStatus !== 'pending_profile_completion';
          break;
        case 3: // Personal Details
        case 4: // Business Information  
        case 5: // Account Preferences
          isAccessible = userStatus === 'pending_profile_completion';
          isCompleted = userStatus === 'active';
          break;
      }

      return {
        ...step,
        isAccessible,
        isCompleted
      };
    });

    setSteps(updatedSteps);

    // Determine current step based on status for designers
    let targetStep = 1;
    if (userStatus === 'pending_profile_completion') {
      targetStep = 3; // Start with Personal Details
    } else if (userStatus === 'active') {
      targetStep = 6; // Dashboard
    }
    
    setCurrentStep(targetStep);
  }, [user]);

  const canAccessStep = (stepNumber: number): boolean => {
    const step = steps.find(s => s.step === stepNumber);
    return step ? step.isAccessible : false;
  };

  const isStepCompleted = (stepNumber: number): boolean => {
    const step = steps.find(s => s.step === stepNumber);
    return step ? step.isCompleted : false;
  };

  const getNextStep = (): number => {
    const currentStepData = steps.find(s => s.step === currentStep);
    if (!currentStepData) return 1;

    // Find the next incomplete step
    const nextStep = steps.find(s => s.step > currentStep && !s.isCompleted);
    return nextStep ? nextStep.step : currentStep;
  };

  const getRequiredStep = (): number => {
    if (!user) return 1;
    
    const userStatus = user.onboardingStatus || 'pending_email_verification';
    
    switch (userStatus) {
      case 'pending_email_verification':
        return 2; // Email verification step
      case 'pending_profile_completion':
        return 3; // Personal details step (first profile step)
      case 'active':
        return 6; // Dashboard
      default:
        return 1;
    }
  };

  // Designer-specific validation functions
  const isEmailVerified = (): boolean => {
    return getEmailVerifiedFlag(user?.emailVerified) === true;
  };

  const isPersonalDetailsComplete = (): boolean => {
    return !!(user?.firstName && user?.lastName);
  };

  const isBusinessDetailsComplete = (): boolean => {
    return !!(user?.businessName);
  };

  const isAccountPreferencesComplete = (): boolean => {
    // This will be determined by the TerritorySelectionFieldset completion
    return user?.onboardingStatus === 'active';
  };

  const canAccessPersonalDetails = (): boolean => {
    return isEmailVerified() && user?.onboardingStatus === 'pending_profile_completion';
  };

  const canAccessBusinessDetails = (): boolean => {
    return canAccessPersonalDetails() && isPersonalDetailsComplete();
  };

  const canAccessAccountPreferences = (): boolean => {
    return canAccessBusinessDetails() && isBusinessDetailsComplete();
  };

  return {
    currentStep,
    steps,
    canAccessStep,
    isStepCompleted,
    getNextStep,
    getRequiredStep,
    userStatus: user?.onboardingStatus || 'pending_email_verification',
    // Designer-specific validations
    isEmailVerified,
    isPersonalDetailsComplete,
    isBusinessDetailsComplete,
    isAccountPreferencesComplete,
    canAccessPersonalDetails,
    canAccessBusinessDetails,
    canAccessAccountPreferences
  };
};
